import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ActivitySection from "@/components/ActivitySection";
import AddressValidationSettings from "@/components/AddressValidationSettings";
import {
  Plus, Trash2, Save, TreePine, CheckCircle2, XCircle,
  Package, ShoppingBag, Users, ChevronDown, ChevronUp, CalendarPlus,
  QrCode, ToggleLeft, ToggleRight, AlertCircle, Download, Shirt, Timer,
  Database, RefreshCw, Clock,
} from "lucide-react";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import { useOffline } from "@/context/OfflineContext";

const TABS = ["Food Distribution", "Sharing Tree", "Check-In", "Coat Distribution", "Dietary Options", "Cache"];

export default function Settings() {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: "#8B918B" }}>Configuration</div>
        <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "Outfit" }}>Settings</h1>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b" style={{ borderColor: "var(--fb-border)" }}>
        {TABS.map((t, i) => (
          <button
            key={t}
            onClick={() => setActiveTab(i)}
            data-testid={i === 4 ? "tab-client-options" : `tab-${t.replace(/\s+/g, "-").toLowerCase()}`}
            className="shrink-0 px-4 py-2.5 text-sm font-semibold rounded-t-lg transition-colors"
            style={{
              color: activeTab === i ? "var(--fb-primary)" : "var(--fb-text-muted)",
              borderBottom: activeTab === i ? "2px solid var(--fb-primary)" : "2px solid transparent",
              background: "transparent",
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {activeTab === 0 && <FoodDistributionTab />}
      {activeTab === 1 && <SharingTreeTab />}
      {activeTab === 2 && <CheckInTab />}
      {activeTab === 2 && <AddressValidationSettings />}
      {activeTab === 3 && <CoatDistributionTab />}
      {activeTab === 4 && <ClientOptionsTab />}
      {activeTab === 5 && <CacheTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Food Distribution Tab
// ---------------------------------------------------------------------------
function FoodDistributionTab() {
  const [items, setItems] = useState([]);
  const [bags, setBags] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [error, setError] = useState("");

  async function load() {
    const [ir, br, mr] = await Promise.all([
      api.get("/food-items"),
      api.get("/bags"),
      api.get("/bag-mappings"),
    ]);
    setItems(ir.data);
    setBags(br.data);
    setMappings(mr.data);
  }

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      {error && <div className="fb-status-error text-sm p-3 rounded-lg">{error}</div>}
      <FoodItemsSection items={items} onReload={load} onError={setError} />
      <BagsSection bags={bags} items={items} onReload={load} onError={setError} />
      <BagMappingsSection mappings={mappings} bags={bags} onReload={load} onError={setError} />
      <ActivitySection targetType="food_item,bag,bag_mapping" title="Recent Food Distribution Changes" limit={20} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Client Options Tab
// ---------------------------------------------------------------------------
function ClientOptionsTab() {
  const { user } = useAuth();
  const [options, setOptions] = useState([]);
  const [newOption, setNewOption] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const canEdit = user?.role !== "viewer";

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/dietary-options");
      setOptions(Array.isArray(data?.options) ? data.options : []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function addOption() {
    const label = newOption.trim();
    if (!label) {
      setError("Enter a dietary restriction option before adding it.");
      return;
    }
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const { data } = await api.post("/dietary-options", { label });
      setOptions(Array.isArray(data?.options) ? data.options : []);
      setNewOption("");
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="client-options-tab">
      <div className="fb-card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
            <Plus className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Dietary Restriction Options</h3>
            <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>
              Add options that staff and clients can select on their profiles.
            </p>
          </div>
        </div>

        {error && <div className="fb-status-error text-sm p-3 rounded-lg mb-4" data-testid="dietary-options-error">{error}</div>}
        {saved && <div className="fb-status-success text-sm p-3 rounded-lg mb-4" data-testid="dietary-options-saved">Dietary restriction option added.</div>}

        {loading ? (
          <div className="text-sm py-4" style={{ color: "var(--fb-text-muted)" }}>Loading options…</div>
        ) : (
          <div className="flex flex-wrap gap-2 mb-5" data-testid="dietary-options-list">
            {options.map((option) => (
              <span
                key={option}
                className="px-3 py-1.5 rounded-full text-sm font-medium"
                style={{ background: "var(--fb-primary)12", color: "var(--fb-primary)" }}
              >
                {option}
              </span>
            ))}
          </div>
        )}

        {canEdit ? (
          <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t" style={{ borderColor: "var(--fb-border)" }}>
            <input
              value={newOption}
              onChange={(e) => { setNewOption(e.target.value); setError(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") addOption(); }}
              placeholder="e.g. No pork"
              maxLength={80}
              className="flex-1 px-3 py-2 rounded-lg border text-sm outline-none focus:border-[#052F66]"
              style={{ borderColor: "var(--fb-border)" }}
              data-testid="dietary-option-input"
            />
            <button
              onClick={addOption}
              disabled={saving}
              className="fb-btn-primary px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 justify-center disabled:opacity-50"
              data-testid="add-dietary-option-btn"
            >
              <Plus className="w-4 h-4" /> {saving ? "Adding…" : "Add option"}
            </button>
          </div>
        ) : (
          <div className="text-sm pt-4 border-t" style={{ color: "var(--fb-text-muted)", borderColor: "var(--fb-border)" }}>
            View-only access: contact an administrator or staff member to add an option.
          </div>
        )}
      </div>

      <ActivitySection targetType="dietary_settings" title="Recent Dietary Option Changes" limit={20} />
    </div>
  );
}

// --- Food Items ---
function FoodItemsSection({ items, onReload, onError }) {
  const [newItem, setNewItem] = useState({ description: "", weight_lbs: "" });
  const [saving, setSaving] = useState(false);

  async function addItem() {
    if (!newItem.description.trim()) return;
    setSaving(true);
    try {
      await api.post("/food-items", {
        description: newItem.description.trim(),
        weight_lbs: parseFloat(newItem.weight_lbs) || 0,
      });
      setNewItem({ description: "", weight_lbs: "" });
      await onReload();
    } catch (e) { onError(formatApiError(e)); }
    finally { setSaving(false); }
  }

  async function deleteItem(id) {
    if (!window.confirm("Delete this food item? It will be removed from bags too.")) return;
    try {
      await api.delete(`/food-items/${id}`);
      await onReload();
    } catch (e) { onError(formatApiError(e)); }
  }

  return (
    <div className="fb-card p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
          <Package className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Food Items</h3>
          <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Items that make up the food bags. Edit names and weights here.</p>
        </div>
      </div>

      <div className="space-y-1 mb-4 max-h-72 overflow-y-auto">
        {items.length > 0 && (
          <div className="grid grid-cols-12 gap-2 px-3 pb-2 text-xs uppercase tracking-wider font-semibold" style={{ color: "#8B918B" }}>
            <div className="col-span-8">Description</div>
            <div className="col-span-3">Weight (lbs)</div>
            <div className="col-span-1" />
          </div>
        )}
        {items.map((item) => (
          <FoodItemRow key={item.id} item={item} onReload={onReload} onError={onError} onDelete={() => deleteItem(item.id)} />
        ))}
        {items.length === 0 && (
          <div className="text-sm text-center py-4" style={{ color: "var(--fb-text-muted)" }}>No items yet.</div>
        )}
      </div>

      <div className="grid grid-cols-12 gap-2 items-center pt-3 border-t" style={{ borderColor: "var(--fb-border)" }}>
        <input
          value={newItem.description}
          onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && addItem()}
          placeholder="New item description"
          className="col-span-8 px-3 py-2 rounded-lg border text-sm"
          style={{ borderColor: "var(--fb-border)" }}
          data-testid="new-item-description"
        />
        <input
          type="number" step="0.25" min="0"
          value={newItem.weight_lbs}
          onChange={(e) => setNewItem({ ...newItem, weight_lbs: e.target.value })}
          placeholder="lbs"
          className="col-span-2 px-3 py-2 rounded-lg border text-sm tabular-nums"
          style={{ borderColor: "var(--fb-border)" }}
          data-testid="new-item-weight"
        />
        <button
          onClick={addItem}
          disabled={saving || !newItem.description.trim()}
          className="col-span-2 fb-btn-primary px-3 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1 justify-center"
          data-testid="add-item-btn"
        >
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
    </div>
  );
}

function FoodItemRow({ item, onReload, onError, onDelete }) {
  const [val, setVal] = useState({ description: item.description, weight_lbs: item.weight_lbs });
  const [dirty, setDirty] = useState(false);

  async function save() {
    try {
      await api.put(`/food-items/${item.id}`, {
        description: val.description,
        weight_lbs: parseFloat(val.weight_lbs) || 0,
      });
      setDirty(false);
      await onReload();
    } catch (e) { onError(formatApiError(e)); }
  }

  return (
    <div className="grid grid-cols-12 gap-2 items-center px-1 py-1">
      <input
        value={val.description}
        onChange={(e) => { setVal({ ...val, description: e.target.value }); setDirty(true); }}
        className="col-span-8 px-3 py-1.5 rounded-lg border text-sm"
        style={{ borderColor: "var(--fb-border)" }}
      />
      <input
        type="number" step="0.25" min="0"
        value={val.weight_lbs}
        onChange={(e) => { setVal({ ...val, weight_lbs: e.target.value }); setDirty(true); }}
        className="col-span-2 px-3 py-1.5 rounded-lg border text-sm tabular-nums"
        style={{ borderColor: "var(--fb-border)" }}
      />
      <div className="col-span-2 flex justify-end gap-1">
        {dirty && (
          <button onClick={save} className="p-1.5 rounded-lg fb-btn-primary" aria-label="Save item">
            <Save className="w-3.5 h-3.5 text-white" />
          </button>
        )}
        <button onClick={onDelete} className="p-1.5 rounded-lg hover:bg-rose-50" aria-label="Delete item">
          <Trash2 className="w-3.5 h-3.5" style={{ color: "var(--fb-error)" }} />
        </button>
      </div>
    </div>
  );
}

// --- Bags ---
function BagsSection({ bags, items, onReload, onError }) {
  const [expanded, setExpanded] = useState({});

  async function addBag() {
    try {
      await api.post("/bags", { name: "New Bag", sort_order: bags.length + 1, bag_weight_lbs: 0, items: [] });
      await onReload();
    } catch (e) { onError(formatApiError(e)); }
  }

  async function deleteBag(id) {
    if (!window.confirm("Delete this bag?")) return;
    try {
      await api.delete(`/bags/${id}`);
      await onReload();
    } catch (e) { onError(formatApiError(e)); }
  }

  function toggle(id) {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  return (
    <div className="fb-card p-4 sm:p-6">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
            <ShoppingBag className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Bag Types</h3>
            <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Define bag contents, quantities, and official weights.</p>
          </div>
        </div>
        <button onClick={addBag} className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border hover:bg-stone-50" style={{ borderColor: "var(--fb-border)" }} data-testid="add-bag-btn">
          <Plus className="w-4 h-4" /> Add Bag
        </button>
      </div>

      <div className="space-y-3">
        {bags.map((bag) => (
          <BagCard
            key={bag.id}
            bag={bag}
            items={items}
            expanded={!!expanded[bag.id]}
            onToggle={() => toggle(bag.id)}
            onDelete={() => deleteBag(bag.id)}
            onReload={onReload}
            onError={onError}
          />
        ))}
        {bags.length === 0 && (
          <div className="text-sm text-center py-4" style={{ color: "var(--fb-text-muted)" }}>No bags configured yet.</div>
        )}
      </div>
    </div>
  );
}

function BagCard({ bag, items, expanded, onToggle, onDelete, onReload, onError }) {
  const [header, setHeader] = useState({ name: bag.name, sort_order: bag.sort_order, bag_weight_lbs: bag.bag_weight_lbs });
  const [bagItems, setBagItems] = useState(bag.items || []);
  const [dirty, setDirty] = useState(false);

  async function save() {
    try {
      await api.put(`/bags/${bag.id}`, {
        name: header.name,
        sort_order: parseInt(header.sort_order) || 0,
        items: bagItems.map((e) => ({ item_id: e.item_id, quantity: parseInt(e.quantity) || 1 })),
      });
      setDirty(false);
      await onReload();
    } catch (e) { onError(formatApiError(e)); }
  }

  function addEntry() {
    if (items.length === 0) return;
    setBagItems([...bagItems, { item_id: items[0].id, quantity: 1, description: items[0].description, weight_lbs: items[0].weight_lbs }]);
    setDirty(true);
  }

  function removeEntry(idx) {
    setBagItems(bagItems.filter((_, i) => i !== idx));
    setDirty(true);
  }

  function updateEntry(idx, field, value) {
    const updated = [...bagItems];
    updated[idx] = { ...updated[idx], [field]: value };
    if (field === "item_id") {
      const found = items.find((it) => it.id === value);
      if (found) { updated[idx].description = found.description; updated[idx].weight_lbs = found.weight_lbs; }
    }
    setBagItems(updated);
    setDirty(true);
  }

  const computedWeight = bagItems.reduce((sum, e) => sum + (parseFloat(e.weight_lbs) || 0) * (parseInt(e.quantity) || 1), 0);

  return (
    <div className="rounded-xl border" style={{ borderColor: "var(--fb-border)" }} data-testid={`bag-card-${bag.id}`}>
      <div className="flex items-center gap-3 p-3 flex-wrap">
        <button onClick={onToggle} className="flex-1 flex items-center gap-2 text-left min-w-0">
          {expanded ? <ChevronUp className="w-4 h-4 shrink-0" style={{ color: "var(--fb-text-muted)" }} /> : <ChevronDown className="w-4 h-4 shrink-0" style={{ color: "var(--fb-text-muted)" }} />}
          <input
            value={header.name}
            onChange={(e) => { setHeader({ ...header, name: e.target.value }); setDirty(true); }}
            onClick={(e) => e.stopPropagation()}
            className="font-semibold bg-transparent border-none outline-none text-sm w-32"
            data-testid={`bag-name-${bag.id}`}
          />
          <span className="text-xs tabular-nums" style={{ color: "var(--fb-text-muted)" }}>
            {computedWeight.toFixed(2)} lbs · {bagItems.length} item type{bagItems.length !== 1 ? "s" : ""}
          </span>
        </button>
        <div className="flex items-center gap-1">
          {dirty && (
            <button onClick={save} className="p-2 rounded-lg fb-btn-primary" aria-label="Save bag" data-testid={`save-bag-${bag.id}`}>
              <Save className="w-4 h-4 text-white" />
            </button>
          )}
          <button onClick={onDelete} className="p-2 rounded-lg hover:bg-rose-50" aria-label="Delete bag">
            <Trash2 className="w-4 h-4" style={{ color: "var(--fb-error)" }} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t px-3 pb-3 pt-2 space-y-2" style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}>
          <div className="grid grid-cols-12 gap-2 text-xs uppercase tracking-wider font-semibold pb-1" style={{ color: "#8B918B" }}>
            <div className="col-span-7">Item</div>
            <div className="col-span-2 text-center">Qty</div>
            <div className="col-span-2 text-right">Weight</div>
            <div className="col-span-1" />
          </div>
          {bagItems.map((entry, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center">
              <select
                value={entry.item_id}
                onChange={(e) => updateEntry(idx, "item_id", e.target.value)}
                className="col-span-7 px-2 py-1.5 rounded-lg border text-sm bg-white"
                style={{ borderColor: "var(--fb-border)" }}
              >
                {items.map((it) => (
                  <option key={it.id} value={it.id}>{it.description}</option>
                ))}
              </select>
              <input
                type="number" min="1"
                value={entry.quantity}
                onChange={(e) => updateEntry(idx, "quantity", e.target.value)}
                className="col-span-2 px-2 py-1.5 rounded-lg border text-sm tabular-nums text-center"
                style={{ borderColor: "var(--fb-border)" }}
              />
              <div className="col-span-2 text-right text-xs tabular-nums" style={{ color: "var(--fb-text-muted)" }}>
                {((parseFloat(entry.weight_lbs) || 0) * (parseInt(entry.quantity) || 1)).toFixed(2)} lbs
              </div>
              <button onClick={() => removeEntry(idx)} className="col-span-1 p-1 rounded hover:bg-rose-50 flex justify-center" aria-label="Remove item">
                <Trash2 className="w-3.5 h-3.5" style={{ color: "var(--fb-error)" }} />
              </button>
            </div>
          ))}
          <button onClick={addEntry} disabled={items.length === 0} className="text-xs font-semibold inline-flex items-center gap-1 mt-1" style={{ color: "var(--fb-primary)" }}>
            <Plus className="w-3 h-3" /> Add item line
          </button>
        </div>
      )}
    </div>
  );
}

// --- Bag-size Mappings ---
function BagMappingsSection({ mappings, bags, onReload, onError }) {
  async function saveMapping(m) {
    try {
      const payload = {
        min_size: parseInt(m.min_size),
        max_size: parseInt(m.max_size),
        bag_ids: m.bag_ids,
      };
      if (m.id) {
        await api.put(`/bag-mappings/${m.id}`, payload);
      } else {
        await api.post("/bag-mappings", payload);
      }
      await onReload();
    } catch (e) { onError(formatApiError(e)); }
  }

  async function deleteMapping(id) {
    if (!window.confirm("Delete this mapping?")) return;
    try {
      await api.delete(`/bag-mappings/${id}`);
      await onReload();
    } catch (e) { onError(formatApiError(e)); }
  }

  async function addMapping() {
    await saveMapping({ min_size: 1, max_size: 2, bag_ids: [] });
  }

  return (
    <div className="fb-card p-4 sm:p-6">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
            <Users className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Household Size → Bag Mapping</h3>
            <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Which bags are assigned at check-in based on household size.</p>
          </div>
        </div>
        <button onClick={addMapping} className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border hover:bg-stone-50" style={{ borderColor: "var(--fb-border)" }} data-testid="add-mapping-btn">
          <Plus className="w-4 h-4" /> Add Rule
        </button>
      </div>

      {mappings.length > 0 && (
        <div className="hidden md:grid grid-cols-12 gap-2 px-3 pb-2 text-xs uppercase tracking-wider font-semibold" style={{ color: "#8B918B" }}>
          <div className="col-span-2">Min Size</div>
          <div className="col-span-2">Max Size</div>
          <div className="col-span-6">Bags Distributed</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
      )}

      <div className="space-y-2">
        {mappings.map((m) => (
          <MappingRow key={m.id} mapping={m} bags={bags} onSave={saveMapping} onDelete={() => deleteMapping(m.id)} />
        ))}
        {mappings.length === 0 && (
          <div className="text-sm text-center py-4" style={{ color: "var(--fb-text-muted)" }}>No mappings yet. Add a rule above.</div>
        )}
      </div>
    </div>
  );
}

function MappingRow({ mapping, bags, onSave, onDelete }) {
  const [m, setM] = useState({ id: mapping.id, min_size: mapping.min_size, max_size: mapping.max_size, bag_ids: mapping.bag_ids || [] });
  const [dirty, setDirty] = useState(false);

  function toggleBag(bid) {
    const ids = m.bag_ids.includes(bid) ? m.bag_ids.filter((b) => b !== bid) : [...m.bag_ids, bid];
    setM({ ...m, bag_ids: ids });
    setDirty(true);
  }

  const assignedNames = bags.filter((b) => m.bag_ids.includes(b.id)).map((b) => b.name).join(" + ") || "—";

  return (
    <div className="grid grid-cols-2 md:grid-cols-12 gap-2 items-center p-3 rounded-xl border" style={{ borderColor: "var(--fb-border)" }}>
      <div className="col-span-1 md:col-span-2">
        <input type="number" min="1" value={m.min_size}
          onChange={(e) => { setM({ ...m, min_size: e.target.value }); setDirty(true); }}
          className="w-full px-3 py-2 rounded-lg border tabular-nums text-sm" style={{ borderColor: "var(--fb-border)" }}
          aria-label="Min household size" />
      </div>
      <div className="col-span-1 md:col-span-2">
        <input type="number" min="1" value={m.max_size}
          onChange={(e) => { setM({ ...m, max_size: e.target.value }); setDirty(true); }}
          className="w-full px-3 py-2 rounded-lg border tabular-nums text-sm" style={{ borderColor: "var(--fb-border)" }}
          aria-label="Max household size" />
      </div>
      <div className="col-span-2 md:col-span-6 flex flex-wrap gap-2 items-center">
        {bags.map((b) => (
          <label key={b.id} className="flex items-center gap-1.5 cursor-pointer text-sm select-none">
            <input type="checkbox" checked={m.bag_ids.includes(b.id)} onChange={() => toggleBag(b.id)} className="accent-[#052F66]" />
            {b.name}
            <span className="text-xs tabular-nums" style={{ color: "var(--fb-text-muted)" }}>({b.bag_weight_lbs} lbs)</span>
          </label>
        ))}
        {bags.length === 0 && <span className="text-xs" style={{ color: "var(--fb-text-muted)" }}>No bags defined</span>}
      </div>
      <div className="col-span-2 md:col-span-2 flex justify-end gap-1">
        {dirty && (
          <button onClick={() => { onSave(m); setDirty(false); }} className="p-2 rounded-lg fb-btn-primary" aria-label="Save mapping">
            <Save className="w-4 h-4 text-white" />
          </button>
        )}
        <button onClick={onDelete} className="p-2 rounded-lg hover:bg-rose-50" aria-label="Delete mapping">
          <Trash2 className="w-4 h-4" style={{ color: "var(--fb-error)" }} />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sharing Tree Tab (existing content)
// ---------------------------------------------------------------------------
function SharingTreeTab() {
  const [tiers, setTiers] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [error, setError] = useState("");
  const [periodError, setPeriodError] = useState("");
  const [advancing, setAdvancing] = useState(false);
  const [advanceMsg, setAdvanceMsg] = useState("");
  const nextYear = new Date().getFullYear() + 1;
  const nextYearExists = periods.some((p) => p.year === nextYear);

  async function load() {
    const [tiersR, periodsR] = await Promise.all([
      api.get("/voucher-tiers"),
      api.get("/sharing-tree/periods"),
    ]);
    setTiers(tiersR.data);
    setPeriods(periodsR.data);
  }
  useEffect(() => { load(); }, []);

  async function advanceYear() {
    if (!window.confirm(
      `Start the ${nextYear} program year?\n\nThis creates a fresh program-year record for ${nextYear} without carrying over any signups from ${nextYear - 1}. You will still need to set signup dates and distribution details in Settings before clients can register.`
    )) return;
    setAdvancing(true);
    setAdvanceMsg("");
    try {
      await api.post("/sharing-tree/advance-year");
      await load();
      setAdvanceMsg(`${nextYear} program year created. Set the signup window and distribution details below.`);
    } catch (e) {
      setAdvanceMsg(formatApiError(e));
    } finally {
      setAdvancing(false);
    }
  }

  async function savePeriod(p) {
    setPeriodError("");
    try {
      await api.put("/sharing-tree/period", {
        year: parseInt(p.year),
        open_date: p.open_date, close_date: p.close_date,
        location_name: p.location_name || "", location_address: p.location_address || "",
        distribution_date: p.distribution_date || "",
        custom_message: p.custom_message || "",
        pickup_start_time: p.pickup_start_time || "",
        pickup_end_time: p.pickup_end_time || "",
        show_family_item: p.show_family_item ?? false,
        mailer_return_instructions: p.mailer_return_instructions || "",
        mailer_contact_phone: p.mailer_contact_phone || "",
      });
      await load();
      return true;
    } catch (e) { setPeriodError(formatApiError(e)); return false; }
  }

  async function saveTier(t) {
    try {
      if (t.id) {
        await api.put(`/voucher-tiers/${t.id}`, { min_size: parseInt(t.min_size), max_size: parseInt(t.max_size), amount: parseFloat(t.amount), label: t.label || "" });
      } else {
        await api.post("/voucher-tiers", { min_size: parseInt(t.min_size), max_size: parseInt(t.max_size), amount: parseFloat(t.amount), label: t.label || "" });
      }
      await load();
    } catch (e) { setError(formatApiError(e)); }
  }

  async function deleteTier(id) {
    if (!window.confirm("Delete this tier?")) return;
    await api.delete(`/voucher-tiers/${id}`);
    await load();
  }

  return (
    <div className="space-y-6">
      <div className="fb-card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
            <TreePine className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Sharing Tree Signup Period</h3>
            <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Configure the signup window per year. Clients can only sign up between these dates.</p>
          </div>
        </div>

        {periods.length > 0 && (
          <div className="space-y-2 mb-5">
            {periods.map((p) => <PeriodRow key={p.id} period={p} onSave={savePeriod} />)}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 mb-4 p-3 rounded-xl border" style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}>
          <div>
            <div className="text-sm font-semibold">Start next program year early</div>
            <div className="text-xs mt-0.5" style={{ color: "var(--fb-text-muted)" }}>
              {nextYearExists
                ? `${nextYear} program year already exists — configure signup dates below.`
                : `Creates a ${nextYear} record without carrying over signups. Signup dates must be set separately.`}
            </div>
          </div>
          <button
            data-testid="advance-year-btn"
            onClick={advanceYear}
            disabled={advancing || nextYearExists}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border shrink-0 disabled:opacity-50 hover:bg-stone-50"
            style={{ borderColor: "var(--fb-border)" }}
          >
            <CalendarPlus className="w-4 h-4" />
            {advancing ? "Creating…" : `Start ${nextYear}`}
          </button>
        </div>
        {advanceMsg && (
          <div className="text-sm p-3 rounded-lg mb-4 fb-status-success" data-testid="advance-year-msg">{advanceMsg}</div>
        )}

        {periodError && <div className="fb-status-error text-sm p-3 rounded-lg mt-3">{periodError}</div>}
      </div>

      <div className="fb-card p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Food Voucher Tiers</h3>
            <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Assigned automatically based on household size during Sharing Tree reporting.</p>
          </div>
          <button data-testid="add-tier-btn" onClick={() => setTiers([...tiers, { id: null, min_size: 1, max_size: 2, amount: 40, label: "New Tier", _new: true }])}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border hover:bg-stone-50"
            style={{ borderColor: "var(--fb-border)" }}>
            <Plus className="w-4 h-4" /> Add Tier
          </button>
        </div>
        <div className="space-y-2">
          {tiers.length > 0 && (
            <div className="hidden md:grid grid-cols-12 gap-2 px-3 pb-2 text-xs uppercase tracking-wider font-semibold" style={{ color: "#8B918B" }}>
              <div className="col-span-4">Tier Label</div>
              <div className="col-span-2">Min</div>
              <div className="col-span-2">Max</div>
              <div className="col-span-2">Voucher ($)</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
          )}
          {tiers.map((t, i) => (
            <TierRow key={t.id || `new-${i}`} tier={t} onSave={saveTier} onDelete={() => t.id && deleteTier(t.id)} />
          ))}
        </div>
        {error && <div className="fb-status-error text-sm p-3 rounded-lg mt-3">{error}</div>}
      </div>

      <ActivitySection targetType="sharing_tree_period,sharing_tree_year,sharing_tree_household" title="Recent Sharing Tree Changes" limit={20} />
    </div>
  );
}

function TierRow({ tier, onSave, onDelete }) {
  const [t, setT] = useState(tier);
  return (
    <div className="grid grid-cols-2 md:grid-cols-12 gap-2 items-center p-3 rounded-xl border" style={{ borderColor: "var(--fb-border)" }} data-testid={`tier-row-${tier.id || "new"}`}>
      <input value={t.label || ""} onChange={(e) => setT({ ...t, label: e.target.value })} placeholder="e.g. Small Household" aria-label="Tier label"
        className="col-span-2 md:col-span-4 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--fb-border)" }} />
      <input type="number" value={t.min_size} onChange={(e) => setT({ ...t, min_size: e.target.value })} placeholder="Min" aria-label="Min"
        className="col-span-1 md:col-span-2 px-3 py-2 rounded-lg border tabular-nums" style={{ borderColor: "var(--fb-border)" }} />
      <input type="number" value={t.max_size} onChange={(e) => setT({ ...t, max_size: e.target.value })} placeholder="Max" aria-label="Max"
        className="col-span-1 md:col-span-2 px-3 py-2 rounded-lg border tabular-nums" style={{ borderColor: "var(--fb-border)" }} />
      <div className="col-span-1 md:col-span-2 relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: "var(--fb-text-muted)" }}>$</span>
        <input type="number" value={t.amount} onChange={(e) => setT({ ...t, amount: e.target.value })} placeholder="Amount" aria-label="Amount"
          className="w-full pl-7 pr-3 py-2 rounded-lg border tabular-nums" style={{ borderColor: "var(--fb-border)" }} />
      </div>
      <div className="col-span-1 md:col-span-2 flex justify-end gap-1">
        <button onClick={() => onSave(t)} className="p-2 rounded-lg fb-btn-primary" aria-label="Save tier"><Save className="w-4 h-4 text-white" /></button>
        {tier.id && <button onClick={onDelete} className="p-2 rounded-lg hover:bg-rose-50" aria-label="Delete tier"><Trash2 className="w-4 h-4" style={{ color: "var(--fb-error)" }} /></button>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Check-In Tab
// ---------------------------------------------------------------------------
function CheckInTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const portalUrl = `${window.location.origin}/portal/login`;
  const qrCanvasRef = useRef(null);

  function downloadQrPng() {
    const qrCanvas = qrCanvasRef.current?.querySelector("canvas");
    if (!qrCanvas) return;

    const padding = 24;
    const labelGap = 16;
    const labelFontSize = 16;
    const urlFontSize = 11;
    const lineHeight = labelFontSize + 6;
    const urlLineHeight = urlFontSize + 5;

    const out = document.createElement("canvas");
    out.width = qrCanvas.width + padding * 2;
    out.height = qrCanvas.height + padding * 2 + labelGap + lineHeight + urlLineHeight;

    const ctx = out.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, out.width, out.height);

    ctx.drawImage(qrCanvas, padding, padding);

    const textY = padding + qrCanvas.height + labelGap;
    ctx.textAlign = "center";
    ctx.fillStyle = "#052F66";
    ctx.font = `600 ${labelFontSize}px 'Outfit', sans-serif`;
    ctx.fillText("Scan to check in", out.width / 2, textY + labelFontSize);

    ctx.font = `${urlFontSize}px monospace`;
    ctx.fillStyle = "#6B7280";
    ctx.fillText(portalUrl, out.width / 2, textY + labelFontSize + urlLineHeight);

    const link = document.createElement("a");
    link.download = "checkin-qr.png";
    link.href = out.toDataURL("image/png");
    link.click();
  }

  async function load() {
    try {
      const { data } = await api.get("/checkin-settings");
      setSettings(data);
    } catch (e) { setError(formatApiError(e)); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true); setError(""); setSaved(false);
    try {
      await api.put("/checkin-settings", settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  }

  function toggleOpen() {
    setSettings({ ...settings, is_open: !settings.is_open });
  }

  function addGroup() {
    const newGroup = {
      id: `g-${Date.now()}`,
      name: "New group",
      max_select: 3,
      items: [],
    };
    setSettings({ ...settings, groups: [...(settings.groups || []), newGroup] });
  }

  function removeGroup(gid) {
    setSettings({ ...settings, groups: settings.groups.filter((g) => g.id !== gid) });
  }

  function updateGroup(gid, patch) {
    setSettings({
      ...settings,
      groups: settings.groups.map((g) => g.id === gid ? { ...g, ...patch } : g),
    });
  }

  function addItem(gid) {
    const item = { id: `i-${Date.now()}`, label: "New item", available: true };
    setSettings({
      ...settings,
      groups: settings.groups.map((g) =>
        g.id === gid ? { ...g, items: [...(g.items || []), item] } : g
      ),
    });
  }

  function removeItem(gid, iid) {
    setSettings({
      ...settings,
      groups: settings.groups.map((g) =>
        g.id === gid ? { ...g, items: g.items.filter((i) => i.id !== iid) } : g
      ),
    });
  }

  function updateItem(gid, iid, patch) {
    setSettings({
      ...settings,
      groups: settings.groups.map((g) =>
        g.id === gid
          ? { ...g, items: g.items.map((i) => i.id === iid ? { ...i, ...patch } : i) }
          : g
      ),
    });
  }

  if (!settings) return <div className="text-sm py-8 text-center" style={{ color: "var(--fb-text-muted)" }}>Loading…</div>;

  return (
    <div className="space-y-6" data-testid="checkin-settings-tab">

      {/* Open / Close toggle */}
      <div className="fb-card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
            <CheckCircle2 className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Check-In Window</h3>
            <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>When open, clients see a "Check In" button on the portal.</p>
          </div>
        </div>

        <div className="flex items-center justify-between p-3 rounded-xl border" style={{ borderColor: "var(--fb-border)" }}>
          <div>
            <div className="font-semibold text-sm">Check-in is {settings.is_open ? "open" : "closed"}</div>
            <div className="text-xs mt-0.5" style={{ color: "var(--fb-text-muted)" }}>
              {settings.is_open ? "Clients can check in via the portal right now." : "Clients will not see the check-in option."}
            </div>
          </div>
          <button
            onClick={toggleOpen}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm"
            style={{
              background: settings.is_open ? "var(--fb-success)20" : "var(--fb-border)",
              color: settings.is_open ? "var(--fb-success)" : "var(--fb-text-muted)",
            }}
            data-testid="checkin-open-toggle"
          >
            {settings.is_open ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
            {settings.is_open ? "Open" : "Closed"}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "#8B918B" }}>
              Opens at (optional)
            </label>
            <input
              type="time"
              value={settings.open_time || ""}
              onChange={(e) => setSettings({ ...settings, open_time: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{ borderColor: "var(--fb-border)" }}
              data-testid="checkin-open-time"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "#8B918B" }}>
              Closes at (optional)
            </label>
            <input
              type="time"
              value={settings.close_time || ""}
              onChange={(e) => setSettings({ ...settings, close_time: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{ borderColor: "var(--fb-border)" }}
              data-testid="checkin-close-time"
            />
          </div>
        </div>
      </div>

      {/* Kiosk reset timer */}
      <div className="fb-card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
            <Timer className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Kiosk Reset Timer</h3>
            <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>How long the kiosk displays a client before returning to the welcome screen.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min="10"
            max="300"
            value={settings.kiosk_reset_seconds ?? 30}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              setSettings({ ...settings, kiosk_reset_seconds: isNaN(val) ? 30 : Math.min(300, Math.max(10, val)) });
            }}
            className="w-24 px-3 py-2 rounded-lg border text-sm text-center tabular-nums"
            style={{ borderColor: "var(--fb-border)" }}
            data-testid="kiosk-reset-seconds"
          />
          <span className="text-sm" style={{ color: "var(--fb-text-muted)" }}>seconds (min 10, max 300)</span>
        </div>
      </div>

      {/* Item groups */}
      <div className="fb-card p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
              <ShoppingBag className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Item Groups</h3>
              <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Groups of items clients can request at check-in.</p>
            </div>
          </div>
          <button
            onClick={addGroup}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border hover:bg-stone-50"
            style={{ borderColor: "var(--fb-border)" }}
            data-testid="add-group-btn"
          >
            <Plus className="w-4 h-4" /> Add Group
          </button>
        </div>

        {settings.groups.length === 0 && (
          <div className="text-sm text-center py-4" style={{ color: "var(--fb-text-muted)" }}>
            No item groups yet. Add a group to let clients make requests.
          </div>
        )}

        <div className="space-y-4">
          {settings.groups.map((group) => (
            <div key={group.id} className="rounded-xl border p-4" style={{ borderColor: "var(--fb-border)" }} data-testid={`group-${group.id}`}>
              {/* Group header */}
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <input
                  value={group.name}
                  onChange={(e) => updateGroup(group.id, { name: e.target.value })}
                  placeholder="Group name"
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg border font-semibold text-sm"
                  style={{ borderColor: "var(--fb-border)" }}
                  aria-label="Group name"
                />
                <div className="flex items-center gap-2">
                  <label className="text-xs font-semibold whitespace-nowrap" style={{ color: "#8B918B" }}>Max select:</label>
                  <input
                    type="number" min="1" max="20"
                    value={group.max_select}
                    onChange={(e) => updateGroup(group.id, { max_select: parseInt(e.target.value) || 1 })}
                    className="w-14 px-2 py-2 rounded-lg border text-sm text-center tabular-nums"
                    style={{ borderColor: "var(--fb-border)" }}
                    aria-label="Max selection"
                  />
                  <button
                    onClick={() => removeGroup(group.id)}
                    className="p-2 rounded-lg hover:bg-rose-50"
                    aria-label="Delete group"
                  >
                    <Trash2 className="w-4 h-4" style={{ color: "var(--fb-error)" }} />
                  </button>
                </div>
              </div>

              {/* Items */}
              <div className="space-y-1.5 mb-3">
                {(group.items || []).length === 0 && (
                  <div className="text-xs text-center py-2" style={{ color: "var(--fb-text-muted)" }}>No items yet.</div>
                )}
                {(group.items || []).map((item) => (
                  <div key={item.id} className="flex flex-wrap items-center gap-2" data-testid={`item-${item.id}`}>
                    <input
                      value={item.label}
                      onChange={(e) => updateItem(group.id, item.id, { label: e.target.value })}
                      placeholder="Item label"
                      className="w-full sm:flex-1 sm:w-auto px-3 py-1.5 rounded-lg border text-sm min-w-0"
                      style={{ borderColor: "var(--fb-border)" }}
                      aria-label="Item label"
                    />
                    <div className="flex items-center gap-3 shrink-0">
                      <label className="flex items-center gap-1.5 text-xs font-medium cursor-pointer select-none whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={item.available !== false}
                          onChange={(e) => updateItem(group.id, item.id, { available: e.target.checked })}
                          className="accent-[#052F66]"
                        />
                        Available
                      </label>
                      <label className="flex items-center gap-1.5 text-xs font-medium cursor-pointer select-none whitespace-nowrap" title="Exempt items don't count toward the Max select limit">
                        <input
                          type="checkbox"
                          checked={item.exempt === true}
                          onChange={(e) => updateItem(group.id, item.id, { exempt: e.target.checked })}
                          className="accent-amber-500"
                          data-testid={`item-exempt-${item.id}`}
                        />
                        Exempt
                      </label>
                      <button
                        onClick={() => removeItem(group.id, item.id)}
                        className="p-1.5 rounded hover:bg-rose-50"
                        aria-label="Remove item"
                      >
                        <Trash2 className="w-3.5 h-3.5" style={{ color: "var(--fb-error)" }} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => addItem(group.id)}
                className="text-xs font-semibold inline-flex items-center gap-1"
                style={{ color: "var(--fb-primary)" }}
                data-testid={`add-item-${group.id}`}
              >
                <Plus className="w-3 h-3" /> Add item
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Custom message */}
      <div className="fb-card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
            <AlertCircle className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Custom Message</h3>
            <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Shown to clients during check-in (e.g. "No eggs today"). Leave blank to hide.</p>
          </div>
        </div>
        <textarea
          rows={3}
          value={settings.custom_message || ""}
          onChange={(e) => setSettings({ ...settings, custom_message: e.target.value })}
          placeholder="Optional message shown to clients above the notes field…"
          className="w-full px-3 py-2 rounded-lg border resize-none text-sm"
          style={{ borderColor: "var(--fb-border)" }}
          data-testid="checkin-custom-message"
        />
      </div>

      {/* Save */}
      {error && <div className="fb-status-error text-sm p-3 rounded-lg">{error}</div>}
      {saved && <div className="fb-status-success text-sm p-3 rounded-lg">Settings saved.</div>}
      <button
        onClick={save}
        disabled={saving}
        className="fb-btn-primary px-6 py-3 rounded-xl font-semibold inline-flex items-center gap-2"
        data-testid="save-checkin-btn"
      >
        <Save className="w-4 h-4" />
        {saving ? "Saving…" : "Save Check-In Settings"}
      </button>

      {/* QR Code */}
      <div className="fb-card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
            <QrCode className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Client Portal QR Code</h3>
            <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Print or display this so clients can scan and check in at the door.</p>
          </div>
        </div>
        <div className="flex flex-col items-center gap-5 py-4">
          <QRCodeSVG
            value={portalUrl}
            size={200}
            bgColor="#FFFFFF"
            fgColor="#052F66"
            level="M"
            data-testid="portal-qr-code"
          />
          <div className="text-center">
            <div className="font-semibold text-sm" style={{ fontFamily: "Outfit" }}>Scan to check in</div>
            <div className="text-xs mt-1 font-mono break-all" style={{ color: "var(--fb-text-muted)" }}>{portalUrl}</div>
          </div>
        </div>
        <div ref={qrCanvasRef} style={{ display: "none" }}>
          <QRCodeCanvas
            value={portalUrl}
            size={400}
            bgColor="#FFFFFF"
            fgColor="#052F66"
            level="M"
          />
        </div>
        <button
          onClick={downloadQrPng}
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border hover:bg-stone-50 mt-2"
          style={{ borderColor: "var(--fb-border)" }}
          data-testid="download-qr-btn"
        >
          <Download className="w-4 h-4" /> Download PNG
        </button>
      </div>

      <ActivitySection targetType="checkin_settings" title="Recent Check-In Setting Changes" limit={20} />
    </div>
  );
}


function computePickupSlotCount(startTime, endTime) {
  if (!startTime || !endTime) return 0;
  try {
    const [hS, mS] = startTime.split(":").map(Number);
    const [hE, mE] = endTime.split(":").map(Number);
    const diff = (hE * 60 + mE) - (hS * 60 + mS);
    return diff > 0 ? Math.floor(diff / 30) : 0;
  } catch {
    return 0;
  }
}

function PeriodRow({ period, onSave }) {
  const [p, setP] = useState({
    year: period.year, open_date: period.open_date || "", close_date: period.close_date || "",
    location_name: period.location_name || "", location_address: period.location_address || "",
    distribution_date: period.distribution_date || "",
    custom_message: period.custom_message || "",
    pickup_start_time: period.pickup_start_time || "",
    pickup_end_time: period.pickup_end_time || "",
    show_family_item: period.show_family_item ?? false,
    mailer_return_instructions: period.mailer_return_instructions || "",
    mailer_contact_phone: period.mailer_contact_phone || "",
  });
  const [dirty, setDirty] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  function setField(k, v) { setP({ ...p, [k]: v }); setDirty(true); }
  async function save() {
    setSaving(true);
    try {
      if (await onSave(p)) { setDirty(false); setExpanded(false); }
    } finally { setSaving(false); }
  }
  const StatusIcon = period.is_open ? CheckCircle2 : XCircle;
  const statusColor = period.is_open ? "var(--fb-success)" : "var(--fb-text-muted)";
  const hasDistInfo = p.location_name || p.location_address || p.distribution_date || p.custom_message || p.pickup_start_time;
  const slotCount = computePickupSlotCount(p.pickup_start_time, p.pickup_end_time);

  return (
    <div className="rounded-xl border p-3" style={{ borderColor: "var(--fb-border)" }} data-testid={`period-row-${period.year}`}>
      <div className="grid grid-cols-2 md:grid-cols-12 gap-2 items-center">
        <div className="col-span-2 md:col-span-2 font-bold tabular-nums text-lg" style={{ fontFamily: "Outfit" }}>{period.year}</div>
        <div className="col-span-1 md:col-span-3">
          <label className="text-[10px] uppercase tracking-wider font-semibold block mb-0.5" style={{ color: "#8B918B" }}>Opens</label>
          <input type="date" value={p.open_date} onChange={(e) => setField("open_date", e.target.value)}
            className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--fb-border)" }} />
        </div>
        <div className="col-span-1 md:col-span-3">
          <label className="text-[10px] uppercase tracking-wider font-semibold block mb-0.5" style={{ color: "#8B918B" }}>Closes</label>
          <input type="date" value={p.close_date} onChange={(e) => setField("close_date", e.target.value)}
            className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--fb-border)" }} />
        </div>
        <div className="col-span-2 md:col-span-4 flex items-center justify-end gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: statusColor }}>
            <StatusIcon className="w-3.5 h-3.5" />{period.is_open ? "Open" : "Closed"}
          </span>
          <button onClick={() => setExpanded(!expanded)} className="text-xs font-semibold underline" style={{ color: "var(--fb-primary)" }}
            data-testid={`expand-period-${period.year}`}>
            {expanded ? "Hide" : "Distribution & mailer"} {hasDistInfo ? "(set)" : ""}
          </button>
          {dirty && (
            <button onClick={save} disabled={saving}
              className="p-2 rounded-lg fb-btn-primary disabled:opacity-50" aria-label="Save period" data-testid={`save-period-${period.year}`}>
              <Save className="w-4 h-4 text-white" />
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t space-y-3" style={{ borderColor: "var(--fb-border)" }}>
          <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "#8B918B" }}>Distribution Details (shown to clients)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input placeholder="Location name" value={p.location_name} onChange={(e) => setField("location_name", e.target.value)} className="px-3 py-2 rounded-lg border" style={{ borderColor: "var(--fb-border)" }} />
            <input placeholder="Address" value={p.location_address} onChange={(e) => setField("location_address", e.target.value)} className="px-3 py-2 rounded-lg border" style={{ borderColor: "var(--fb-border)" }} />
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold block mb-0.5" style={{ color: "#8B918B" }}>Distribution date</label>
              <input type="date" value={p.distribution_date} onChange={(e) => setField("distribution_date", e.target.value)} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--fb-border)" }} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold block mb-0.5" style={{ color: "#8B918B" }}>Distribution time — from</label>
              <input type="time" value={p.pickup_start_time} onChange={(e) => setField("pickup_start_time", e.target.value)}
                className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--fb-border)" }} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold block mb-0.5" style={{ color: "#8B918B" }}>Distribution time — to</label>
              <input type="time" value={p.pickup_end_time} onChange={(e) => setField("pickup_end_time", e.target.value)}
                className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--fb-border)" }} />
            </div>
          </div>
          {slotCount > 0 && (
            <div className="text-xs px-3 py-2 rounded-lg" style={{ background: "#F0F4FA", color: "var(--fb-primary)" }}>
              → <strong>{slotCount} pickup window{slotCount !== 1 ? "s" : ""}</strong> of 30 min each will be auto-assigned to new signups.
            </div>
          )}
          <textarea placeholder="Custom message (optional)" rows={2} value={p.custom_message} onChange={(e) => setField("custom_message", e.target.value)}
            className="w-full px-3 py-2 rounded-lg border resize-none" style={{ borderColor: "var(--fb-border)" }} />
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={p.show_family_item}
              onChange={(e) => setField("show_family_item", e.target.checked)}
              className="w-4 h-4 accent-[#052F66] shrink-0"
            />
            <span className="text-sm font-medium">Show "Family item" field on signup form</span>
          </label>
          <div className="pt-3 border-t space-y-2" style={{ borderColor: "var(--fb-border)" }}>
            <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--fb-text-muted)" }}>Paper signup mailer — per year</div>
            <p className="text-xs" style={{ color: "var(--fb-text-muted)" }}>Printed on the paper form. Include the mailing address or drop-off location and how to return it. The signup close date above is used as the return deadline.</p>
            <label className="block text-sm font-medium" htmlFor={`mailer-return-${period.year}`}>Return instructions and address</label>
            <textarea id={`mailer-return-${period.year}`} value={p.mailer_return_instructions}
              onChange={(e) => setField("mailer_return_instructions", e.target.value)}
              placeholder="Mail completed forms to… or drop off at…" rows={3} maxLength={500}
              className="w-full px-3 py-2 rounded-lg border bg-white"
              style={{ borderColor: "var(--fb-border)" }} data-testid={`mailer-return-${period.year}`} />
            <label className="block text-sm font-medium" htmlFor={`mailer-phone-${period.year}`}>Questions? Contact phone</label>
            <input id={`mailer-phone-${period.year}`} type="tel" value={p.mailer_contact_phone}
              onChange={(e) => setField("mailer_contact_phone", e.target.value)}
              placeholder="Phone number for paper signup questions" maxLength={80}
              className="w-full px-3 py-2 rounded-lg border bg-white"
              style={{ borderColor: "var(--fb-border)" }} data-testid={`mailer-phone-${period.year}`} />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Coat Distribution Tab
// ---------------------------------------------------------------------------
function CoatDistributionTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/coat-settings");
      setSettings(data);
    } catch (e) { setError(formatApiError(e)); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true); setError(""); setSaved(false);
    try {
      const { data } = await api.put("/coat-settings", {
        open_date: settings.open_date || "",
        close_date: settings.close_date || "",
        force_open: settings.force_open,
      });
      setSettings(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  }

  if (!settings) return <div className="text-sm py-8 text-center" style={{ color: "var(--fb-text-muted)" }}>Loading…</div>;

  const isActive = settings.is_active;

  return (
    <div className="space-y-6" data-testid="coat-settings-tab">
      <div className="fb-card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
            <Shirt className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Coat Distribution Season</h3>
            <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>
              Set the season window. Outside these dates the coat pickup card is hidden on client records. Defaults to October–March if unset.
            </p>
          </div>
        </div>

        {/* Live status chip */}
        <div className="flex items-center gap-2 mb-5">
          <span
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full"
            style={{
              background: isActive ? "var(--fb-success)20" : "var(--fb-border)",
              color: isActive ? "var(--fb-success)" : "var(--fb-text-muted)",
            }}
            data-testid="coat-season-status"
          >
            <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-500 animate-pulse" : "bg-stone-400"}`} />
            {isActive
              ? `Season active${settings.season_label ? ` · ${settings.season_label}` : ""}`
              : "Season inactive"}
          </span>
          {settings.force_open && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: "#FEF3C720", color: "#D97706" }}>
              Force-open
            </span>
          )}
        </div>

        {/* Date range */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "#8B918B" }}>
              Season opens
            </label>
            <input
              type="date"
              value={settings.open_date || ""}
              onChange={(e) => setSettings({ ...settings, open_date: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{ borderColor: "var(--fb-border)" }}
              data-testid="coat-open-date"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "#8B918B" }}>
              Season closes
            </label>
            <input
              type="date"
              value={settings.close_date || ""}
              onChange={(e) => setSettings({ ...settings, close_date: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{ borderColor: "var(--fb-border)" }}
              data-testid="coat-close-date"
            />
          </div>
        </div>

        {/* Force-open toggle */}
        <div className="flex items-center justify-between p-3 rounded-xl border" style={{ borderColor: "var(--fb-border)" }}>
          <div>
            <div className="font-semibold text-sm">Force season open</div>
            <div className="text-xs mt-0.5" style={{ color: "var(--fb-text-muted)" }}>
              Override dates — coat pickup is always active regardless of date range.
            </div>
          </div>
          <button
            onClick={() => setSettings({ ...settings, force_open: !settings.force_open })}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm"
            style={{
              background: settings.force_open ? "var(--fb-success)20" : "var(--fb-border)",
              color: settings.force_open ? "var(--fb-success)" : "var(--fb-text-muted)",
            }}
            data-testid="coat-force-open-toggle"
          >
            {settings.force_open ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
            {settings.force_open ? "On" : "Off"}
          </button>
        </div>
      </div>

      {error && <div className="fb-status-error text-sm p-3 rounded-lg">{error}</div>}
      {saved && <div className="fb-status-success text-sm p-3 rounded-lg">Coat distribution settings saved.</div>}

      <button
        onClick={save}
        disabled={saving}
        className="fb-btn-primary px-6 py-3 rounded-xl font-semibold inline-flex items-center gap-2"
        data-testid="save-coat-settings-btn"
      >
        <Save className="w-4 h-4" />
        {saving ? "Saving…" : "Save Coat Settings"}
      </button>

      <ActivitySection targetType="coat_settings,coat_distribution" title="Recent Coat Distribution Changes" limit={20} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cache Tab
// ---------------------------------------------------------------------------
function formatRelativeTime(isoString) {
  if (!isoString) return null;
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? "s" : ""} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr !== 1 ? "s" : ""} ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay} day${diffDay !== 1 ? "s" : ""} ago`;
}

function CacheTab() {
  const { isOnline, lastSynced, isCacheWarming, triggerWarmCache, privacyRestricted } = useOffline();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const syncedLabel = formatRelativeTime(lastSynced);
  const syncedDate = lastSynced ? new Date(lastSynced).toLocaleString() : null;

  return (
    <div className="space-y-6" data-testid="cache-tab">
      {privacyRestricted && (
        <div className="fb-status-warning p-3 rounded-lg text-sm">
          Offline client access is disabled for this account’s client privacy settings.
        </div>
      )}
      <div className="fb-card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
            <Database className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Offline Client Cache</h3>
            <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>
              Pre-loads the full client list so staff can work without an internet connection.
            </p>
          </div>
        </div>

        <div className="rounded-xl border p-4 flex items-start justify-between gap-4 flex-wrap"
          style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}>
          <div className="flex items-start gap-3">
            <Clock className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--fb-text-muted)" }} />
            <div>
              <div className="text-sm font-semibold" style={{ color: "var(--fb-text)" }}>
                Last synced
              </div>
              {syncedLabel ? (
                <div className="text-sm mt-0.5" style={{ color: "var(--fb-text-muted)" }}
                  data-testid="cache-last-synced">
                  {syncedLabel}
                  <span className="ml-2 text-xs" style={{ color: "#8B918B" }}>({syncedDate})</span>
                </div>
              ) : (
                <div className="text-sm mt-0.5" style={{ color: "var(--fb-text-muted)" }}
                  data-testid="cache-last-synced">
                  Never — cache has not been populated yet
                </div>
              )}
            </div>
          </div>

          <button
            data-testid="warm-cache-btn"
            onClick={triggerWarmCache}
            disabled={!isOnline || isCacheWarming || privacyRestricted}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
            style={{
              background: isOnline && !isCacheWarming ? "var(--fb-primary)" : "var(--fb-border)",
              color: isOnline && !isCacheWarming ? "#fff" : "var(--fb-text-muted)",
              cursor: !isOnline || isCacheWarming ? "not-allowed" : "pointer",
            }}
          >
            <RefreshCw className={`w-4 h-4 ${isCacheWarming ? "animate-spin" : ""}`} />
            {isCacheWarming ? "Refreshing…" : "Refresh now"}
          </button>
        </div>

        {!isOnline && (
          <div className="mt-3 text-sm rounded-lg px-4 py-3"
            style={{ background: "#F1F5F9", color: "#475569", border: "1px solid #CBD5E1" }}>
            You're offline — connect to the internet to refresh the cache.
          </div>
        )}

        <div className="mt-6 space-y-2">
          <h4 className="text-sm font-semibold" style={{ color: "var(--fb-text)" }}>How it works</h4>
          <ul className="text-sm space-y-1.5 list-disc list-inside" style={{ color: "var(--fb-text-muted)" }}>
            <li>The cache is automatically refreshed each time you open the app while online.</li>
            <li>All client records are downloaded in pages and stored locally in your browser.</li>
            <li>When you go offline, the Clients page falls back to this cached data.</li>
            <li>Use <strong>Refresh now</strong> before a distribution event to ensure the latest records are available.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
